import dayjs, { type Dayjs } from "dayjs";
import { useMemo } from "react";

import { useContractContext } from "@/contexts/ContractContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import {
  getNextPaymentDueDate,
  getSubscriptionType,
  isFluxPayContract,
  isFreePlanContract,
} from "@/utils/contract";

export interface HeaderContractWarningsState {
  isFreePlan: boolean;
  shouldShowTrialWarning: boolean;
  hasPassed30Days: boolean;
  isFreeTrialContract: boolean;
  isBusWithoutNextPayment: boolean;
  daysRemaining: number | null;
  trialWarningTooltip: string;
  trialWarningLabel: string;
}

function resolveNextPayment(
  isFreeTrialContract: boolean,
  contractIssuedDate: Dayjs,
  nextPaymentDueDateExtension: string | undefined,
  typeOrganization: string | undefined
): Dayjs | null {
  if (isFreeTrialContract && contractIssuedDate.isValid()) {
    return contractIssuedDate.add(30, "day");
  }

  if (nextPaymentDueDateExtension) {
    return dayjs(nextPaymentDueDateExtension);
  }

  if (typeOrganization === "bus" && contractIssuedDate.isValid()) {
    return contractIssuedDate.add(30, "day");
  }

  return null;
}

function formatDaysRemainingLabel(daysRemaining: number | null): string {
  const days = daysRemaining ?? 0;
  const suffix = days === 1 ? "dia restante" : "dias restantes";
  return `Teste grátis: ${days} ${suffix}`;
}

function buildTrialWarningTooltip(
  hasPassed30Days: boolean,
  isFreeTrialContract: boolean,
  isBusWithoutNextPayment: boolean
): string {
  if (hasPassed30Days) {
    return "O período de teste grátis de 30 dias foi concluído. Realize uma nova assinatura para continuar utilizando o sistema.";
  }

  if (isFreeTrialContract || isBusWithoutNextPayment) {
    return "Após 30 dias do início do período grátis será necessário realizar uma nova assinatura.";
  }

  return "Após esse período será debitado em sua conta o valor do plano solicitado.";
}

function buildTrialWarningLabel(
  hasPassed30Days: boolean,
  isFreeTrialContract: boolean,
  isBusWithoutNextPayment: boolean,
  daysRemaining: number | null
): string {
  if (hasPassed30Days) {
    return "Renove sua assinatura";
  }

  if (isFreeTrialContract || isBusWithoutNextPayment) {
    return formatDaysRemainingLabel(daysRemaining);
  }

  return formatDaysRemainingLabel(daysRemaining);
}

export function useHeaderContractWarnings(): HeaderContractWarningsState {
  const { typeOrganization } = useOrganizationContext();
  const { access } = useSessionContext();
  const { currentContract } = useContractContext();
  const contract = currentContract?.[0]?.resource;

  return useMemo(() => {
    const isFreePlan = isFreePlanContract(contract);
    const contractIssuedDate = dayjs(contract?.term?.[0]?.issued);
    const isFluxPay = isFluxPayContract(contract);
    const subscriptionTypeExtension = getSubscriptionType(contract);

    const isFreeTrialContract =
      typeOrganization === "bus" &&
      Boolean(
        subscriptionTypeExtension?.includes(
          "Profissional autônomo - Período grátis"
        )
      );

    const nextPaymentDueDateExtension = getNextPaymentDueDate(contract);

    const isBusWithoutNextPayment =
      typeOrganization === "bus" &&
      !nextPaymentDueDateExtension &&
      !isFluxPay &&
      !isFreeTrialContract;

    const nextPayment = resolveNextPayment(
      isFreeTrialContract,
      contractIssuedDate,
      nextPaymentDueDateExtension,
      typeOrganization
    );

    const daysSinceIssued =
      (isFreeTrialContract || typeOrganization === "bus") &&
        contractIssuedDate.isValid()
        ? dayjs().diff(contractIssuedDate, "day")
        : null;

    const daysRemaining = nextPayment ? nextPayment.diff(dayjs(), "day") : null;
    const hasPassed30Days = daysSinceIssued !== null && daysSinceIssued >= 30;

    const shouldShowTrialWarning = (() => {
      if (access?.type !== "professional" || !currentContract) {
        return false;
      }

      if (isFreePlan) {
        return false;
      }

      if (isFluxPay) {
        return false;
      }

      if (isFreeTrialContract) {
        return contractIssuedDate.isValid();
      }

      if (isBusWithoutNextPayment) {
        return contractIssuedDate.isValid();
      }

      if (!nextPayment) {
        return false;
      }

      const daysUntilPayment = nextPayment.diff(contractIssuedDate, "day");
      return (
        daysUntilPayment <= 15 && daysRemaining !== null && daysRemaining > 0
      );
    })();

    return {
      isFreePlan,
      shouldShowTrialWarning,
      hasPassed30Days,
      isFreeTrialContract,
      isBusWithoutNextPayment,
      daysRemaining,
      trialWarningTooltip: buildTrialWarningTooltip(
        hasPassed30Days,
        isFreeTrialContract,
        isBusWithoutNextPayment
      ),
      trialWarningLabel: buildTrialWarningLabel(
        hasPassed30Days,
        isFreeTrialContract,
        isBusWithoutNextPayment,
        daysRemaining
      ),
    };
  }, [access?.type, contract, currentContract, typeOrganization]);
}
